import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { VariantVersion } from '../models/variant-version';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoColumnFixingModule, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-variant-versions',
    templateUrl: './developer-variant-versions.component.html',
    styleUrls: ['./developer-variant-versions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class DeveloperVariantVersionsComponent implements OnInit, AfterViewInit {
    variantVersions: VariantVersion[] = [];
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) { }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_VariantVersion_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_VariantVersion_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_VariantVersion_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.variantVersions = [];

            this.variantVersions = await this.developerService.getVariantVersions();
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = { VariantVersionsId: null, VariantVersionName: null } as VariantVersion;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as VariantVersion, item => this.saveVariantVersion(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as VariantVersion, item => this.saveVariantVersion(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as VariantVersion, item => this.removeVariantVersion(item));
    };

    async saveVariantVersion(variantVersion: VariantVersion): Promise<VariantVersion> {
        const variantVersionExist = this.variantVersions.find(i => i.VariantVersionName === variantVersion.VariantVersionName && i.VariantVersionsId !== variantVersion.VariantVersionsId);
        if (variantVersionExist != null) {
            throw new Error(`Another variant version with that name already exists. Name of the existing variant version: ${variantVersionExist.VariantVersionName}`);
        }

        const newVariantVersion = await this.developerService.saveVariantVersion(variantVersion);
        this.toastService.showToast('VariantVersion saved...', ToastType.success);
        return newVariantVersion;
    }

    async removeVariantVersion(variantVersion: VariantVersion): Promise<VariantVersion> {
        await this.developerService.deleteVariantVersion(variantVersion.VariantVersionsId);
        this.toastService.showToast('VariantVersion deleted...', ToastType.success);
        return variantVersion;
    }
}
